import type { GetOrganizationsResponse, Organization } from '@/lib/api/types/multi-orgs.types';
import { HttpError } from '@/lib/https';
import { switchOrganization } from '@/modules/auth/services/auth.service';
import { useAuthStore } from '@/state/store/auth';

export const DEFAULT_ORGANIZATION_ID = 'default';

const isOrganizationEnabled = (org: Organization) => org.isEnable !== false;

export const isOrgAccessRevokedError = (error: unknown): boolean => {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
};

/**
 * `/iam/organizations/my` already lists only the user's own organizations. It is not narrowed further by
 * the organizations on `/iam/me`: those reflect the access token's claims and can leave out organizations
 * the user really belongs to, which hid them from the switcher.
 */
const getAvailableOrganizations = (orgsData: GetOrganizationsResponse | undefined): Organization[] =>
  (orgsData?.organizations ?? []).filter(isOrganizationEnabled);

export const isCurrentOrgUnavailable = (
  selectedOrgId: string | null,
  orgsData: GetOrganizationsResponse | undefined
): boolean => {
  if (!selectedOrgId || selectedOrgId === DEFAULT_ORGANIZATION_ID) {
    return false;
  }

  const availableOrganizations = getAvailableOrganizations(orgsData);
  if (availableOrganizations.length === 0) {
    return false;
  }

  return !availableOrganizations.some((org) => org.itemId === selectedOrgId);
};

export const shouldRecoverOrgAccess = (
  selectedOrgId: string | null,
  orgsError: unknown,
  orgsData: GetOrganizationsResponse | undefined
): boolean => {
  if (!selectedOrgId || selectedOrgId === DEFAULT_ORGANIZATION_ID) {
    return false;
  }

  if (orgsError && isOrgAccessRevokedError(orgsError)) {
    return true;
  }

  return isCurrentOrgUnavailable(selectedOrgId, orgsData);
};

export const recoverOrgAccessBySwitchingToDefault = async (): Promise<boolean> => {
  const { selectedOrgId, setTokens, refreshToken } = useAuthStore.getState();

  if (!selectedOrgId || selectedOrgId === DEFAULT_ORGANIZATION_ID) {
    return false;
  }

  const response = await switchOrganization(DEFAULT_ORGANIZATION_ID);

  setTokens({
    accessToken: response.access_token,
    refreshToken: (response.refresh_token || refreshToken) ?? '',
  });

  window.location.reload();
  return true;
};
